// Shopify adapter — wraps all Shopify Storefront API calls.
// Extracted from store-context.jsx so store-context.jsx can be provider-agnostic.
import { createStorefrontApiClient } from '@shopify/storefront-api-client'

const CART_FRAGMENT = `
  id
  checkoutUrl
  totalQuantity
  cost {
    subtotalAmount { amount currencyCode }
    totalAmount { amount currencyCode }
  }
  lines(first: 100) {
    nodes {
      id
      quantity
      cost { totalAmount { amount currencyCode } }
      merchandise {
        ... on ProductVariant {
          id
          title
          selectedOptions { name value }
          product {
            id title handle
            featuredImage { id url altText width height }
          }
        }
      }
    }
  }
`

function makeClient() {
  return createStorefrontApiClient({
    storeDomain: process.env.GATSBY_SHOPIFY_STORE_URL,
    publicAccessToken: process.env.GATSBY_STOREFRONT_ACCESS_TOKEN,
    apiVersion: '2024-04',
  })
}

const shopifyAdapter = {
  makeClient,

  async getCart(cartId) {
    const client = makeClient()
    const { data } = await client.request(
      `query getCart($cartId: ID!) {
        cart(id: $cartId) { ${CART_FRAGMENT} }
      }`,
      { variables: { cartId } }
    )
    return data?.cart ?? null
  },

  async createCart() {
    const client = makeClient()
    const { data } = await client.request(
      `mutation cartCreate {
        cartCreate {
          cart { ${CART_FRAGMENT} }
          userErrors { field message }
        }
      }`
    )
    if (data?.cartCreate?.userErrors?.length) {
      throw new Error(data.cartCreate.userErrors[0].message)
    }
    return data.cartCreate.cart
  },

  async addLines(cart, lines) {
    const cartId = cart.id
    const client = makeClient()
    const { data } = await client.request(
      `mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
        cartLinesAdd(cartId: $cartId, lines: $lines) {
          cart { ${CART_FRAGMENT} }
          userErrors { field message }
        }
      }`,
      { variables: { cartId, lines } }
    )
    if (data?.cartLinesAdd?.userErrors?.length) {
      throw new Error(data.cartLinesAdd.userErrors[0].message)
    }
    return data.cartLinesAdd.cart
  },

  async updateLine(cart, lineId, quantity) {
    const cartId = cart.id
    const client = makeClient()
    const { data } = await client.request(
      `mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
        cartLinesUpdate(cartId: $cartId, lines: $lines) {
          cart { ${CART_FRAGMENT} }
          userErrors { field message }
        }
      }`,
      { variables: { cartId, lines: [{ id: lineId, quantity }] } }
    )
    if (data?.cartLinesUpdate?.userErrors?.length) {
      throw new Error(data.cartLinesUpdate.userErrors[0].message)
    }
    return data.cartLinesUpdate.cart
  },

  async removeLine(cart, lineId) {
    const cartId = cart.id
    const client = makeClient()
    const { data } = await client.request(
      `mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
        cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
          cart { ${CART_FRAGMENT} }
          userErrors { field message }
        }
      }`,
      { variables: { cartId, lineIds: [lineId] } }
    )
    if (data?.cartLinesRemove?.userErrors?.length) {
      throw new Error(data.cartLinesRemove.userErrors[0].message)
    }
    return data.cartLinesRemove.cart
  },

  // In Shopify mode, checkoutUrl is already set on the cart
  async checkout(cart) {
    if (cart?.checkoutUrl) {
      window.location.href = cart.checkoutUrl
    } else {
      throw new Error('Checkout URL not available')
    }
  },
}

export default shopifyAdapter
